import React, { useState, useEffect, useRef } from 'react';
import LogoMain from './LogoMain.png';
import { useOktaAuth } from '@okta/okta-react';
import EpicForm from './EpicForm';
import EpicFromFileForm from './EpicFromFileForm';
import IssueForm from './IssueForm';
import PullRequestForm from './PullRequestForm';
import DiscoveryForm from './DiscoveryForm';
import AITestingForm from './AITestingForm';
import SdlcFlow from './SdlcFlow';
import UserProfile from './components/UserProfile';
import UserProfileDev from './components/UserProfile-dev';
import { ProjectSelector } from './ProjectSelector';

const TabContainer = ({ onHide, selectedModel, initialTab }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [activeTab, setActiveTab] = useState(initialTab || 'story');
  const [sharedProject, setSharedProject] = useState(() => {
    // Load saved project from localStorage on initialization
    return localStorage.getItem('selectedProject') || null;
  }); // Centralized project state
  const [issueTypes, setIssueTypes] = useState([]); // Issue types for selected project
  // Projects are now managed by ProjectSelector component internally
  const [userInfo, setUserInfo] = useState(null); // User information state
  // Both development and production use Okta authentication
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isDevMode = isLocalhost; // Development mode = localhost (for UI differences only)
  const issueFormRef = useRef(null); // Reference to IssueForm to refresh epics

    // Project fetching is now handled by ProjectSelector component

  // Get user information from Okta for both dev and production
  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth.getUser().then(info => {
        setUserInfo({
          name: info.name,
          email: info.email
        });
      }).catch(err => {
        console.error('Error getting user info:', err);
        // Don't set fallback email - let forms handle missing user info
        setUserInfo({
          name: 'User',
          email: null
        });
      });
    }
  }, [authState, oktaAuth]);

  // Project validation is now handled by ProjectSelector component

  // Fetch issue types when project changes
  useEffect(() => {
    const fetchIssueTypes = async () => {
      if (sharedProject && authState?.accessToken) {
        try {
          const token = typeof authState.accessToken === 'string' ? authState.accessToken : authState.accessToken?.accessToken;
          const response = await fetch(`/api/issue-types/${sharedProject}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          const data = await response.json();
          if (data.success) {
            setIssueTypes(data.issueTypes || []);
          }
        } catch (error) {
          console.error('Error fetching issue types:', error);
          setIssueTypes([]);
        }
      } else {
        setIssueTypes([]);
      }
    };

    fetchIssueTypes();
  }, [sharedProject, authState]);

  // Handle project change and save to localStorage
  const handleProjectChange = (projectKey) => {
    setSharedProject(projectKey);
    if (projectKey) {
      localStorage.setItem('selectedProject', projectKey);
    } else {
      localStorage.removeItem('selectedProject');
    }
  };

  const handleEpicSubmit = (epicData) => {
    console.log('Epic submitted:', epicData);
    // Refresh the epics list in IssueForm after epic creation
    if (issueFormRef.current && issueFormRef.current.refreshEpics) {
      issueFormRef.current.refreshEpics();
    }
  };

  const handleStorySubmit = (storyData) => {
    console.log('Story submitted:', storyData);
  };

  // Handler for coming soon features
  const handleComingSoon = () => {
    alert('Coming Soon');
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoContainer}>
            <img src={LogoMain} alt="RapidAI" style={styles.logo} />
          </div>
          <p style={styles.subtitle}>Tool for Intelligent Development</p>
        </div>
        <div style={styles.headerRight}>
          {isDevMode ? <UserProfileDev /> : <UserProfile />}
          <button onClick={onHide} style={styles.hideButton}>Close</button>
        </div>
      </div>

      {/* Project Selector */}
      <div style={styles.projectSelectorContainer}>
        <ProjectSelector
          selectedProject={sharedProject}
          onProjectChange={handleProjectChange}
        />
      </div>

      <div style={styles.mainContent}>
        <div style={styles.tabBar}>
          <button
            style={{ ...styles.tabButton, ...(activeTab === 'discovery' ? styles.activeTab : styles.inactiveTab) }}
            onClick={() => setActiveTab('discovery')}
          >
            🔍 Discovery
          </button>
          <button
            style={{ ...styles.tabButton, ...styles.inactiveTab, opacity: 0.6 }}
            onClick={handleComingSoon}
          >
            🤝 Collab
          </button>
          <button
            style={{ ...styles.tabButton, ...styles.inactiveTab, opacity: 0.6 }}
            onClick={handleComingSoon}
          >
            🔗 Jama
          </button>
          <button
            style={{ ...styles.tabButton, ...(activeTab === 'epic' ? styles.activeTab : styles.inactiveTab), display: 'none' }}
            onClick={() => setActiveTab('epic')}
          >
            📋 Create Epic
          </button>
          <button
            style={{ ...styles.tabButton, ...(activeTab === 'epicfile' ? styles.activeTab : styles.inactiveTab), display: 'none' }}
            onClick={() => setActiveTab('epicfile')}
          >
            📂 Epic from File
          </button>
          <button
            style={{ ...styles.tabButton, ...(activeTab === 'story' ? styles.activeTab : styles.inactiveTab) }}
            onClick={() => setActiveTab('story')}
          >
            📝 Design Output
          </button>
          <button
            style={{ ...styles.tabButton, ...styles.inactiveTab, opacity: 0.4, cursor: 'not-allowed' }}
            onClick={(e) => e.preventDefault()}
            disabled
          >
            ⚙️ Engineering
          </button>
          <button
            style={{ ...styles.tabButton, ...styles.inactiveTab, opacity: 0.6 }}
            onClick={handleComingSoon}
          >
            💻 Development
          </button>
          <button
            style={{ ...styles.tabButton, ...(activeTab === 'pullrequest' ? styles.activeTab : styles.inactiveTab) }}
            onClick={() => setActiveTab('pullrequest')}
          >
            🔀 Code Ai
          </button>
          <button
            style={{ ...styles.tabButton, ...(activeTab === 'testing' ? styles.activeTab : styles.inactiveTab) }}
            onClick={() => setActiveTab('testing')}
          >
            🧪 Testing
          </button>
          <button
            style={{ ...styles.tabButton, ...(activeTab === 'qadoc' ? styles.activeTab : styles.inactiveTab) }}
            onClick={() => setActiveTab('qadoc')}
          >
            📑 SDLC
          </button>
        </div>
        
        <div style={styles.tabContent}>
          <div style={{ display: activeTab === 'discovery' ? 'block' : 'none' }}>
            <DiscoveryForm selectedProject={sharedProject} onProjectChange={handleProjectChange} selectedModel={selectedModel} userInfo={userInfo} />
          </div>
          <div style={{ display: activeTab === 'epic' ? 'block' : 'none' }}>
            <EpicForm onSubmit={handleEpicSubmit} selectedProject={sharedProject} selectedModel={selectedModel} userInfo={userInfo} />
          </div>
          <div style={{ display: activeTab === 'epicfile' ? 'block' : 'none' }}>
            <EpicFromFileForm selectedProject={sharedProject} selectedModel={selectedModel} userInfo={userInfo} />
          </div>
          <div style={{ display: activeTab === 'story' ? 'block' : 'none' }}>
            <IssueForm ref={issueFormRef} onSubmit={handleStorySubmit} selectedProject={sharedProject} selectedModel={selectedModel} userInfo={userInfo} issueTypes={issueTypes} />
          </div>
          {/* Temporarily deactivated - Engineering tab content
          <div style={{ display: activeTab === 'engineering' ? 'block' : 'none' }}>
            <EngineeringReviewForm selectedProject={sharedProject} onProjectChange={handleProjectChange} selectedModel={selectedModel} />
          </div>
          */}
          <div style={{ display: activeTab === 'pullrequest' ? 'block' : 'none' }}>
            <PullRequestForm selectedProject={sharedProject} isActive={activeTab === 'pullrequest'} selectedModel={selectedModel} />
          </div>
          <div style={{ display: activeTab === 'testing' ? 'block' : 'none' }}>
            <AITestingForm selectedProject={sharedProject} selectedModel={selectedModel} userInfo={userInfo} />
          </div>
          <div style={{ display: activeTab === 'qadoc' ? 'block' : 'none' }}>
            <SdlcFlow selectedProject={sharedProject} selectedModel={selectedModel} userInfo={userInfo} />
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '2rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    padding: '1rem 0',
    borderBottom: '2px solid #e9ecef',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  logo: {
    height: '40px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
  title: {
    fontSize: '2.5rem',
    color: '#333',
    marginBottom: '0.5rem',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#666',
    marginBottom: '0',
  },
  projectSelectorContainer: {
    margin: '0 0 2rem 0',
    padding: '1.5rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  projectSelectorTitle: {
    fontSize: '1.2rem',
    color: '#333',
    marginBottom: '1rem',
    borderBottom: '2px solid #eee',
    paddingBottom: '0.5rem',
  },
  projectInputs: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-end',
  },
  inputGroup: {
    flex: 1,
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: '#555',
    fontSize: '0.9rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    boxSizing: 'border-box',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    boxSizing: 'border-box',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: 'white',
  },
  errorText: {
      color: '#dc3545',
      marginBottom: '1rem',
  },
  tabContainer: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  tabButtons: {
    display: 'flex',
    overflowX: 'auto',
    marginBottom: '1rem',
    borderBottom: '2px solid #ddd',
    scrollbarWidth: 'thin',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  tabBar: {
    display: 'flex',
    overflowX: 'auto',
    marginBottom: '1rem',
    borderBottom: '2px solid #ddd',
    scrollbarWidth: 'thin',
  },
  tabButton: {
    padding: '1rem 2rem',
    border: 'none',
    borderRight: '1px solid #ccc',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  activeTab: {
    backgroundColor: '#007bff',
    color: '#fff',
    borderBottom: '2px solid #0056b3',
  },
  inactiveTab: {
    backgroundColor: '#e9ecef',
    color: '#495057',
  },
  tabContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'visible',
    border: '1px solid #ddd',
    minHeight: '600px',
    padding: '2rem',
  },
  hideButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export default TabContainer;